import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Landing_page.css';
import Announcement1 from '../images/AskHRAdminPortal-Wide (3).png';
import { Toast, ConfirmDialog, useToast, useConfirm } from './ToastNotification.js';

export default function Landing_page({
    userRoles = [],
    navbarRef,
    onPinnedReport,
    onAuditReport,
    onAnalytics,
    onServiceNow,
}) {
    const modulesRef = useRef(null);
    const navigate = useNavigate();
    const [localUserRoles, setLocalUserRoles] = useState(userRoles);
    const { toast, showToast, hideToast } = useToast();
    const { confirmDialog, showConfirm } = useConfirm();

    useEffect(() => {
        fetchUserRoles();
    }, []);

    useEffect(() => {
    // Fire-and-forget: warms the Graph API cache in the background
    // By the time user navigates to Role Assignment, data is ready
    fetch('/api/prefetch-graph-data', { credentials: 'include' })
        .then(r => r.json())
        .then(d => console.log('[Prefetch] Graph data warm-up:', d))
        .catch(err => console.warn('[Prefetch] Warm-up failed (non-critical):', err));
    }, []); 

    async function fetchUserRoles() {
        try {
            const response = await fetch('/api/my-roles', {
                method: 'GET',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setLocalUserRoles(data.roles || []);
                console.log('✅ Landing page roles loaded:', data.roles);
            }
        } catch (error) {
            console.error('Error fetching user roles:', error);
        }
    }

    function isEnabled(feature) {
        if (localUserRoles.length === 0) return false;
        switch (feature) {
            case 'superadmin': return localUserRoles.includes('System Administrator');
            case 'broadcast': return localUserRoles.includes('Broadcast');
            case 'analytics': return localUserRoles.includes('AskHR Analytics');
            case 'casemanagement': return localUserRoles.includes('Case Management');
            case 'pinnedreport': return localUserRoles.includes('Pinned Users Report');
            default: return false;
        }
    }

    function handleAnalyticsClick() {
        window.open(
            'https://app.powerbi.com/groups/924a3a94-5167-47c4-917a-bfeb101a3d5b/reports/68ad8d96-a7db-4dc4-8613-c17b9f31ae34/da69fe9b86888e804563?experience=power-bi',
            '_blank'
        );
    }

    function handleServiceNowConsole() {
        window.open(
            'https://fasttracktest.service-now.com/now/nav/ui/classic/params/target/u_hr_support_request_list.do%3Fsysparm_userpref_module%3D2ee78f968796aa544029ef0a0cbb3550%26sysparm_fixed_query%3Dassignment_group%3Djavascript%3AgetMyGroups()%5Eactive%3Dtrue%5Estate!%3D6',
            '_blank'
        );
    }

    function handleServiceNowDashboard() {
        window.open(
            'https://fasttracktest.service-now.com/now/nav/ui/classic/params/target/%24pa_dashboard.do%3Fsysparm_dashboard%3D74cfcc7e978bfa14aad9f29fe153af82',
            '_blank'
        );
    }

    async function handlePinnedUsersReport() {
        const confirmDownload = await showConfirm(
            'This will download the pinned users audit report. Do you want to continue?'
        );
        if (!confirmDownload) return;

        try {
            const response = await fetch('/api/pinned-audit-report/download', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to download pinned users report');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const today = new Date().toISOString().split('T')[0];
            a.download = `Pinned_Users_Audit_Report_${today}.xlsx`;

            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            showToast('Pinned users report downloaded successfully!', 'success');
        } catch (error) {
            console.error('❌ Error downloading pinned users report:', error);
            showToast('Failed to download pinned users report: ' + error.message, 'error');
        }
    }

    const modules = [
        {
            key: 'broadcast',
            icon: '📢',
            tag: 'Communications',
            title: 'Broadcast',
            desc: 'Send targeted announcements to selected audience. Track delivery via audit reports.',
            feature: 'broadcast',
            subLinks: [
                { label: 'Message', isRouterLink: true, to: '/broadcastmessage' },
                { label: 'Audit Report', isRouterLink: true, to: '/broadcastreport' },
            ],
        },
        {
            key: 'askhr',
            icon: '📊',
            tag: 'Insights',
            title: 'AskHR Analytics',
            desc: 'Visualize AskHR usage, Queries & responses, Queries per document, Bot Metrics',
            feature: 'analytics',
            onClick: handleAnalyticsClick,
        },
        {
            key: 'case',
            icon: '📁',
            tag: 'Operations',
            title: 'Case Management',
            desc: 'Process HR Requests via ServiceNow Console, View insights via Dashboard',
            feature: 'casemanagement',
            subLinks: [
                { label: 'HR Admin Console', onClick: handleServiceNowConsole },
                { label: 'Dashboard', onClick: handleServiceNowDashboard },
            ],
        },
        {
            key: 'sysadmin',
            icon: '⚙️',
            tag: 'Administration',
            title: 'System Administrator',
            desc: 'Manage role assignments to control access.',
            feature: 'superadmin',
            isRouterLink: true,
            to: '/role-assignment',
        },
        {
            key: 'pinned',
            icon: '📌',
            tag: 'Reports',
            title: 'Pinned Users Report',
            desc: 'Increase AskHR by analyzing users to whom AskHR may not be available',
            feature: 'pinnedreport',
            onClick: handlePinnedUsersReport,
        },
    ];

    const visibleModules = modules.filter(mod => isEnabled(mod.feature));

    return (
        <div className="landing-wrapper">

            <section className="lp-hero">
                <div className="lp-hero-grid" />
                <div className="lp-hero-glow" />
                <div className="lp-announce-wrapper">
                    <div className="lp-announce-header">
                        <img
                            src={Announcement1}
                            alt="Announcement Broadcast"
                            className="lp-announce-logo"
                        />
                    </div>
                </div>
            </section>

            <section className="lp-modules" ref={modulesRef}>
                <div className="lp-grid" style={{
                    gridTemplateColumns: `repeat(${visibleModules.length}, 1fr)`
                }}>
                    {visibleModules.map((mod) => {

                        const inner = (
    <>
        <div className="lp-card-arrow">↗</div>
        <div className="lp-card-icon">{mod.icon}</div>
        <div className="lp-card-tag">{mod.tag}</div>
        <h3>{mod.title}</h3>
        <p>{mod.desc}</p>

        {mod.subLinks && (
            <div className="lp-pills">
                {mod.subLinks.map((sub) => (
                    <span
                        key={sub.label}
                        className="lp-pill lp-pill-link"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (sub.isRouterLink) {
                                navigate(sub.to);
                            } else {
                                sub.onClick();
                            }
                        }}
                    >
                        {sub.label}
                    </span>
                ))}
            </div>
        )}
    </>
);

                        // Cards with subLinks — card itself not clickable
                        if (mod.subLinks) {
                            return (
                                <div key={mod.key} className="lp-card">
                                    {inner}
                                </div>
                            );
                        }

                        // Router link cards
                        if (mod.isRouterLink) {
                            return (
                                <Link
                                    key={mod.key}
                                    to={mod.to}
                                    style={{ textDecoration: 'none', display: 'block' }}
                                >
                                    <div className="lp-card">{inner}</div>
                                </Link>
                            );
                        }

                        // onClick cards
                        return (
                            <div key={mod.key} className="lp-card" onClick={mod.onClick}>
                                {inner}
                            </div>
                        );
                    })}
                </div>

                <div className="lp-section-top">
                    <h2>**Your access depends on roles assigned to you</h2>
                </div>
            </section>

            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={hideToast} />
            )}
            {confirmDialog && (
                <ConfirmDialog
                    message={confirmDialog.message}
                    onConfirm={confirmDialog.onConfirm}
                    onCancel={confirmDialog.onCancel}
                />
            )}
        </div>
    );
}